<template>
  <div class="container mt-4">
    <TopNav>
      <NavItem
        icon="far fa-save"
        :text="$t('GENERAL.BUTTON.SAVE')"
        :pulse="isDirty"
        :loading="inProgress"
        @click.native="updateCustomWaiver"
      />
    </TopNav>
    <div class="row mt-4 pb-2 d-flex">
      <div class="col h1 mb-0 pb-0">
        {{ $t('SETTINGS.TITLE.CUSTOM_WAIVER') }}
      </div>
      <div class="col align-self-center">
        <div class="float-right" v-if="teacher">
          <HMSwitch v-model="waiverEnabled" />
        </div>
      </div>
    </div>
    <hr class="my-4" />
    <div class="row mt-4 mb-4">
      <div class="col-10 p">
        {{ $t('SETTINGS.TEXT.CUSTOM_WAIVER_INFO') }}
        <a :href="config.customWaiverHelpURL" target="_blank">
          {{ $t('SETTINGS.TEXT.CUSTOM_WAIVER_LINK') }}
        </a>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <HMHTMLEditor
          v-model="waiverNew"
          :htmlValue="waiverOld"
          :title="$t('SETTINGS.TITLE.CUSTOM_WAIVER')"
        />
      </div>
    </div>
    <hr class="my-4" />
    <h2>{{ $t('SETTINGS.TITLE.WAIVER_VERSION_HISTORY') }}</h2>
    <HMPaginatedTable
      ref="waiversTable"
      name="waivers-table"
      api="setting/getWaivers"
      :fields="waiverTableFields"
      class="mt-3"
    >
      <template slot="title" slot-scope="{ data }">
        <div class="c-brand-color pointer" @click="openWaiverModal(data)">
          {{ data.title }}
        </div>
      </template>
      <template slot="accepted" slot-scope="{ data }">
        <div
          class="c-brand-color"
          :class="{ pointer: data.signed_customers > 0 }"
          @click="openSignersModal(data)"
        >
          {{ data.signed_customers }}/{{ data.total_customers }}
        </div>
      </template>
      <template slot="created" slot-scope="{ data }">
        {{ $formatDate(data.created) }}
      </template>
    </HMPaginatedTable>
  </div>
</template>

<script>
import TopNav from '@/web/components/navigation/TopNav';
import NavItem from '@/web/components/navigation/NavItem.vue';
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import editorToolbar from '@/web/utils/editor-toolbar';
import { renderHtmlFromEditorJS } from '@/core/utils/utils';
import { getConfig } from '@/web/utils/configuration';
import WaiverSignersModal from './WaiverSignersModal';
import WaiverShowModal from './WaiverShowModal';
import DirtyMixin from '../../../mixins/DirtyMixin';
import { errorToMessage } from '../../../../core/utils/errors';

export default Vue.extend({
  components: {
    TopNav,
    NavItem,
  },
  mixins: [DirtyMixin],
  data() {
    return {
      customToolbar: editorToolbar,
      inProgress: false,
      formError: null,
      waiverOld: null,
      waiverNew: null,
    };
  },
  computed: {
    ...mapGetters('user', ['teacher']),
    ...mapState('setting', ['waiver']),
    waiverEnabled: {
      get() {
        return this.teacher.is_waiver_enabled;
      },
      set(value) {
        this.$store.dispatch('user/toggleWaiver', value);
      },
    },
    config() {
      return getConfig();
    },
    waiverTableFields() {
      return [
        {
          name: 'title',
          title: this.$t('SETTINGS.TABLE.TITLE'),
        },
        {
          name: 'accepted',
          title: this.$t('SETTINGS.TABLE.ACCEPTED'),
        },
        {
          name: 'created',
          title: this.$t('SETTINGS.TABLE.CREATED'),
        },
      ];
    },
  },
  methods: {
    async updateCustomWaiver() {
      if (!this.isDirty) return;
      this.inProgress = true;
      try {
        this.formError = null;
        await this.$store.dispatch('setting/updateWaiver', {
          waiver: this.waiverOld || renderHtmlFromEditorJS(this.waiverNew),
          waiver_new: this.waiverNew,
        });
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.CUSTOM_WAIVER_SUCCESSFULLY_SAVED'
          ),
        });
        this.$store.dispatch('globals/setDirty', false);
        this.refreshWaiversTable();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to save your waiver',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
        });
      } finally {
        this.inProgress = false;
      }
    },
    refreshWaiversTable() {
      this.$refs.waiversTable.refresh();
    },
    async getWaiver() {
      await this.$store.dispatch('setting/getWaiver');
      this.waiverOld = this.waiver.waiver;
      this.waiverNew = this.waiver.waiver_new;
    },
    async openSignersModal(waiver) {
      if (waiver.signed_customers === 0) return;
      this.$modal.show(
        WaiverSignersModal,
        {
          waiver: waiver,
        },
        {
          classes: ['vm--inline-modal'],
        }
      );
    },
    openWaiverModal(waiver) {
      this.$modal.show(
        WaiverShowModal,
        {
          waiver: waiver,
        },
        {}
      );
    },
  },
  mounted() {
    this.getWaiver();
  },
  beforeDestroy() {
    this.$modal.hideAll();
  },
  watch: {
    waiverOld: {
      handler(waiver, oldWaiver) {
        if (!oldWaiver || waiver !== oldWaiver) {
          this.setDirty();
        }
      },
      deep: true,
    },
    waiverNew: {
      handler(waiver, oldWaiver) {
        if (!oldWaiver || waiver !== oldWaiver) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
});
</script>

<style></style>
